<script setup lang="ts">
import { computed } from "vue";

// Icon geometry copied from lucide (https://lucide.dev, ISC licensed) rather
// than taking a runtime dependency for the handful of icons this app uses.
// To add an icon: copy its shapes from the lucide source into ICON_SHAPES.
// Shapes are modelled as data instead of raw markup so nothing needs v-html.

type LucideIconName =
  | "chevron-down"
  | "circle-alert"
  | "circle-help"
  | "external-link"
  | "grid-2x2"
  | "mail"
  | "moon"
  | "pencil"
  | "plus"
  | "rows-3"
  | "save"
  | "settings"
  | "sun"
  | "trash"
  | "x";

type IconShape =
  | { kind: "path"; d: string }
  | { kind: "circle"; cx: number; cy: number; r: number };

const ICON_SHAPES: Record<LucideIconName, IconShape[]> = {
  "chevron-down": [{ kind: "path", d: "m6 9 6 6 6-6" }],
  "circle-alert": [
    { kind: "circle", cx: 12, cy: 12, r: 10 },
    { kind: "path", d: "M12 8v4" },
    { kind: "path", d: "M12 16h.01" },
  ],
  "circle-help": [
    { kind: "circle", cx: 12, cy: 12, r: 10 },
    { kind: "path", d: "M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" },
    { kind: "path", d: "M12 17h.01" },
  ],
  "external-link": [
    { kind: "path", d: "M15 3h6v6M21 3l-9 9" },
    {
      kind: "path",
      d: "M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4",
    },
  ],
  "grid-2x2": [
    {
      kind: "path",
      d: "M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z",
    },
    { kind: "path", d: "M3 12h18M12 3v18" },
  ],
  mail: [
    {
      kind: "path",
      d: "M22 7v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7m20 0a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2m20 0-9 6-9-6",
    },
  ],
  moon: [{ kind: "path", d: "M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" }],
  pencil: [
    {
      kind: "path",
      d: "M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z",
    },
    { kind: "path", d: "m15 5 4 4" },
  ],
  plus: [
    { kind: "path", d: "M5 12h14" },
    { kind: "path", d: "M12 5v14" },
  ],
  "rows-3": [{ kind: "path", d: "M3 6h18M3 12h18M3 18h18" }],
  save: [
    {
      kind: "path",
      d: "M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h12l4 4v12a2 2 0 0 1-2 2Z",
    },
    { kind: "path", d: "M17 21v-8H7v8M7 3v5h8" },
  ],
  trash: [
    { kind: "path", d: "M3 6h18" },
    { kind: "path", d: "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" },
    { kind: "path", d: "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" },
  ],
  x: [
    { kind: "path", d: "M18 6 6 18" },
    { kind: "path", d: "m6 6 12 12" },
  ],
  sun: [
    { kind: "circle", cx: 12, cy: 12, r: 4 },
    { kind: "path", d: "M12 2v2" },
    { kind: "path", d: "M12 20v2" },
    { kind: "path", d: "m4.93 4.93 1.41 1.41" },
    { kind: "path", d: "m17.66 17.66 1.41 1.41" },
    { kind: "path", d: "M2 12h2" },
    { kind: "path", d: "M20 12h2" },
    { kind: "path", d: "m6.34 17.66-1.41 1.41" },
    { kind: "path", d: "m19.07 4.93-1.41 1.41" },
  ],
  settings: [
    {
      kind: "path",
      d: "M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z",
    },
    { kind: "circle", cx: 12, cy: 12, r: 3 },
  ],
};

const props = withDefaults(
  defineProps<{
    name: LucideIconName;
    size?: number;
    strokeWidth?: number;
  }>(),
  { size: 20, strokeWidth: 2 }
);

const shapes = computed(() => ICON_SHAPES[props.name]);
</script>

<template>
  <svg
    xmlns="http://www.w3.org/2000/svg"
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <template v-for="(shape, index) in shapes" :key="index">
      <circle
        v-if="shape.kind === 'circle'"
        :cx="shape.cx"
        :cy="shape.cy"
        :r="shape.r"
      />
      <path v-else :d="shape.d" />
    </template>
  </svg>
</template>
